import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config/api';
import toast from 'react-hot-toast';

const RESEND_COOLDOWN = 30; // seconds
const OTP_MAX_ATTEMPTS = 5;
const OTP_BLOCK_MS = 30 * 60 * 1000;

const getResendAttempts = (mobile) => {
  try {
    const raw = localStorage.getItem(`otp_resend_limit_${mobile}`);
    if (!raw) return { count: 0, blockedAt: null };
    const parsed = JSON.parse(raw);
    if (parsed.blockedAt && Date.now() - parsed.blockedAt > OTP_BLOCK_MS) {
      localStorage.removeItem(`otp_resend_limit_${mobile}`);
      return { count: 0, blockedAt: null };
    }
    return parsed;
  } catch {
    return { count: 0, blockedAt: null };
  }
};

const incrementResendAttempts = (mobile) => {
  const current = getResendAttempts(mobile);
  const newCount = current.count + 1;
  const updated = {
    count: newCount,
    blockedAt: newCount >= OTP_MAX_ATTEMPTS ? Date.now() : null,
  };
  localStorage.setItem(`otp_resend_limit_${mobile}`, JSON.stringify(updated));
  return updated;
};

const OTPVerification = ({ mobile, onLoginSuccess, onBack }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
  const timerRef = useRef(null);

  const startCountdown = () => {
    setCountdown(RESEND_COOLDOWN);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    startCountdown();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOTPChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOTP = [...otp];
      newOTP[index] = value;
      setOtp(newOTP);
      setError('');

      // Auto focus next input
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`).focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Extract only digits
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (digits.length > 0) {
      const newOTP = [...otp];
      digits.split('').forEach((digit, index) => {
        if (index < 6) {
          newOTP[index] = digit;
        }
      });
      setOtp(newOTP);
      setError('');
      
      // If all 6 digits filled, auto submit
      if (digits.length === 6) {
        setTimeout(() => {
          document.querySelector('button[type="submit"]').click();
        }, 100);
      } else {
        // Focus the next empty field
        const nextIndex = Math.min(digits.length, 5);
        document.getElementById(`otp-${nextIndex}`)?.focus();
      }
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      setError('Please enter complete OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('=== VERIFYING OTP ===');
      const formattedPhoneNumber = `+91${mobile}`;
      console.log('Phone number:', formattedPhoneNumber);
      console.log('OTP:', otpString);
      
      // Verify OTP with backend API
      console.log('Sending to backend:', `${API_BASE_URL}/users/auth/otp`);
      const requestBody = { phNum: formattedPhoneNumber, otp: otpString };
      console.log('Request body:', requestBody);
      
      const response = await fetch(`${API_BASE_URL}/users/auth/otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('Backend response status:', response.status);
      console.log('Backend response data:', data);

      if (!response.ok || !data.success) {
        console.error('Backend error:', data.message);
        throw new Error(data.message || 'Authentication failed');
      }

      // Extract user and token from data
      const userData = data.data?.user || data.user;
      const userToken = data.data?.token || data.token;

      if (userData && userToken) {
        console.log('=== LOGIN SUCCESSFUL ===');
        console.log('User:', userData);
        console.log('JWT Token:', userToken);
        console.log('=======================');
        
        // Normalize user data (map ph_num to mobile)
        const normalizedUser = {
          ...userData,
          mobile: userData.ph_num || userData.mobile
        };
        
        toast.success('Login successful!');
        // onLoginSuccess ke through login karo — token bhi pass karo
        // (seedha login() mat bulao — double call hoti thi aur token missing tha)
        onLoginSuccess(normalizedUser, userToken);
      } else {
        console.error('Invalid response from server:', data);
        console.error('Expected user and token but got:', { userData, userToken });
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Verification error:', err);
      
      if (err.message) {
        setError(err.message);
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    // Client-side rate limit check (separate 5-attempt quota for resends)
    const attempts = getResendAttempts(mobile);
    if (attempts.count >= OTP_MAX_ATTEMPTS) {
      const elapsed = attempts.blockedAt ? Date.now() - attempts.blockedAt : 0;
      const minsLeft = Math.max(1, Math.ceil((OTP_BLOCK_MS - elapsed) / 60000));
      setError(`Resend limit reached. Please wait ${minsLeft} minute${minsLeft !== 1 ? 's' : ''}.`);
      return;
    }

    setResending(true);
    setError('');
    setOtp(['', '', '', '', '', '']);

    try {
      const response = await fetch(`${API_BASE_URL}/users/auth/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phNum: `+91${mobile}` }),
      });

      const data = await response.json();

      if (response.status === 429) {
        setError('Too many attempts. Please wait 10 minutes before requesting again.');
        return;
      }

      if (!response.ok || !data.success) {
        setError(data.message || 'Failed to resend OTP. Please try again.');
        return;
      }

      const updated = incrementResendAttempts(mobile);
      const remaining = OTP_MAX_ATTEMPTS - updated.count;
      startCountdown();
      toast.success(
        remaining > 0
          ? `OTP resent! (${remaining} resend${remaining !== 1 ? 's' : ''} remaining)`
          : 'OTP resent! No more resends available.'
      );

      // Focus first input
      setTimeout(() => document.getElementById('otp-0')?.focus(), 100);
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-6 animate-scaleIn">
      <button
        onClick={onBack}
        className="flex items-center text-slate-500 hover:text-slate-700 transition mb-4 animate-slideDown"
        disabled={loading}
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      <div className="text-center mb-6">
        <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
          Verify OTP
        </h3>
        <p className="text-sm text-slate-500">
          Enter the 6-digit code sent to <span className="font-semibold text-slate-700">+91 {mobile}</span>
        </p>
      </div>

      <form onSubmit={handleVerifyOTP} className="space-y-6">
        <div className="flex justify-center gap-2 sm:gap-3" onPaste={handlePaste}>
          {otp.map((digit, index) => (
            <input
              key={index}
              id={`otp-${index}`}
              type="text"
              inputMode="numeric"
              value={digit}
              onChange={(e) => handleOTPChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-12 h-14 sm:w-14 sm:h-16 text-center text-xl sm:text-2xl font-bold border-2 border-slate-200 rounded-lg bg-slate-50 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none focus:bg-white transition duration-200 hover:border-slate-300"
              maxLength="1"
              required
              disabled={loading}
            />
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm text-center">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || otp.join('').length !== 6}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold text-lg py-3 rounded-2xl transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying...
            </>
          ) : (
            'Verify OTP'
          )}
        </button>

        <div className="text-center space-y-2">
          {countdown > 0 ? (
            <p className="text-sm text-slate-400">
              Resend OTP in <span className="font-semibold text-blue-600">{countdown}s</span>
            </p>
          ) : (
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
              onClick={handleResendOTP}
              disabled={loading || resending}
            >
              {resending ? (
                <span className="flex items-center justify-center gap-1">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              ) : (
                "Didn't receive OTP? Resend"
              )}
            </button>
          )}
          <div>
            <button
              type="button"
              className="text-xs text-slate-400 hover:text-slate-600 transition"
              onClick={onBack}
              disabled={loading || resending}
            >
              Change Number
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OTPVerification;
